import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    listRecipes,
    saveRecipe,
    deleteRecipe,
    getKey,
    setKey,
    clearKey,
    ApiError,
    type Recipe,
    type RecipeDraft,
} from './api';

type View =
    | { name: 'list' }
    | { name: 'detail'; id: string }
    | { name: 'edit'; id: string | null };

const emptyDraft: RecipeDraft = {
    title: '',
    servings: '',
    totalTime: '',
    source: '',
    notes: '',
    ingredients: '',
    method: '',
    tags: '',
};

const toDraft = (recipe: Recipe): RecipeDraft => ({
    id: recipe.id,
    title: recipe.title,
    servings: recipe.servings,
    totalTime: recipe.totalTime,
    source: recipe.source,
    notes: recipe.notes,
    ingredients: recipe.ingredients.join('\n'),
    method: recipe.method.join('\n'),
    tags: recipe.tags.join('\n'),
});

export function App() {
    const [unlocked, setUnlocked] = useState(() => getKey() !== null);
    const [recipes, setRecipes] = useState<Recipe[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [view, setView] = useState<View>({ name: 'list' });
    const [filter, setFilter] = useState('');

    const load = useCallback(async () => {
        setError(null);
        try {
            const { recipes: loaded } = await listRecipes();
            setRecipes(loaded);
        } catch (caught) {
            if (caught instanceof ApiError && caught.status === 401) {
                clearKey();
                setUnlocked(false);
                setError('That key was not accepted.');
                return;
            }
            setError(caught instanceof Error ? caught.message : 'The API did not respond.');
        }
    }, []);

    useEffect(() => {
        if (unlocked) void load();
    }, [unlocked, load]);

    const shown = useMemo(() => {
        if (!recipes) return [];
        const needle = filter.trim().toLowerCase();
        if (!needle) return recipes;
        return recipes.filter((r) =>
            r.title.toLowerCase().includes(needle) ||
            r.tags.some((t) => t.includes(needle)) ||
            r.ingredients.some((i) => i.toLowerCase().includes(needle)),
        );
    }, [recipes, filter]);

    if (!unlocked) {
        return (
            <Shell>
                <KeyGate
                    error={error}
                    onSubmit={(key) => {
                        setKey(key);
                        setError(null);
                        setUnlocked(true);
                    }}
                />
            </Shell>
        );
    }

    const current =
        view.name === 'detail' ? recipes?.find((r) => r.id === view.id) ?? null : null;
    const editing =
        view.name === 'edit' && view.id
            ? recipes?.find((r) => r.id === view.id) ?? null
            : null;

    return (
        <Shell>
            {error && <p className="alert">{error}</p>}

            {view.name === 'list' && (
                <ListView
                    recipes={shown}
                    total={recipes?.length ?? null}
                    filter={filter}
                    onFilter={setFilter}
                    onOpen={(id) => setView({ name: 'detail', id })}
                    onNew={() => setView({ name: 'edit', id: null })}
                />
            )}

            {view.name === 'detail' && current && (
                <DetailView
                    recipe={current}
                    onBack={() => setView({ name: 'list' })}
                    onEdit={() => setView({ name: 'edit', id: current.id })}
                    onDelete={async () => {
                        if (!window.confirm(`Delete “${current.title}”?`)) return;
                        try {
                            await deleteRecipe(current.id);
                            setView({ name: 'list' });
                            await load();
                        } catch (caught) {
                            setError(caught instanceof Error ? caught.message : 'Delete failed.');
                        }
                    }}
                />
            )}

            {view.name === 'edit' && (
                <Editor
                    initial={editing ? toDraft(editing) : emptyDraft}
                    onCancel={() =>
                        setView(editing ? { name: 'detail', id: editing.id } : { name: 'list' })
                    }
                    onSave={async (draft) => {
                        const saved = await saveRecipe(draft);
                        await load();
                        setView({ name: 'detail', id: saved.id });
                    }}
                />
            )}
        </Shell>
    );
}

// ── Layout ───────────────────────────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
    return (
        <main className="shell">
            <p className="eyebrow">
                mossforge / <b>recipe-store</b>
            </p>
            {children}
        </main>
    );
}

function KeyGate({
    error,
    onSubmit,
}: {
    error: string | null;
    onSubmit: (key: string) => void;
}) {
    const [value, setValue] = useState('');

    return (
        <>
            <h1>Recipes</h1>
            {error && <p className="alert">{error}</p>}
            <label className="field">
                <span>Shared key</span>
                <input
                    type="password"
                    value={value}
                    autoFocus
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && value.trim()) onSubmit(value);
                    }}
                />
            </label>
            <div className="actions">
                <button
                    className="primary"
                    disabled={!value.trim()}
                    onClick={() => onSubmit(value)}
                >
                    Unlock
                </button>
            </div>
            <p className="hint">Stored in this browser only. You will not be asked again here.</p>
        </>
    );
}

// ── List ─────────────────────────────────────────────────────────────────────

function ListView({
    recipes,
    total,
    filter,
    onFilter,
    onOpen,
    onNew,
}: {
    recipes: Recipe[];
    total: number | null;
    filter: string;
    onFilter: (value: string) => void;
    onOpen: (id: string) => void;
    onNew: () => void;
}) {
    return (
        <>
            <h1>Recipes</h1>

            <div className="toolbar">
                <input
                    className="search"
                    placeholder="Search title, tag or ingredient"
                    value={filter}
                    onChange={(e) => onFilter(e.target.value)}
                />
                <button className="primary" onClick={onNew}>
                    Add
                </button>
            </div>

            {total === null && <p className="hint">Loading…</p>}

            {total === 0 && (
                <p className="hint">
                    Nothing saved yet. <b>Add</b> puts the first one in.
                </p>
            )}

            {total !== null && total > 0 && recipes.length === 0 && (
                <p className="hint">No recipe matches that.</p>
            )}

            {recipes.length > 0 && (
                <div className="ledger">
                    {recipes.map((recipe) => (
                        <button
                            key={recipe.id}
                            className="row rowButton"
                            onClick={() => onOpen(recipe.id)}
                        >
                            <span className="key">{recipe.title}</span>
                            <span className="val">{recipe.tags.join(' · ')}</span>
                            <span className="note">
                                {[recipe.totalTime, recipe.servings].filter(Boolean).join(' · ')}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </>
    );
}

// ── Detail ───────────────────────────────────────────────────────────────────

function DetailView({
    recipe,
    onBack,
    onEdit,
    onDelete,
}: {
    recipe: Recipe;
    onBack: () => void;
    onEdit: () => void;
    onDelete: () => void;
}) {
    return (
        <>
            <button className="back" onClick={onBack}>
                ← All recipes
            </button>

            <h1>{recipe.title}</h1>

            <p className="meta">
                {[
                    recipe.servings && `Serves ${recipe.servings}`,
                    recipe.totalTime,
                    recipe.tags.join(' · '),
                ]
                    .filter(Boolean)
                    .join('  ·  ')}
            </p>

            {recipe.ingredients.length > 0 && (
                <section>
                    <h2>Ingredients</h2>
                    <ul className="plain">
                        {recipe.ingredients.map((line, index) => (
                            <li key={index}>{line}</li>
                        ))}
                    </ul>
                </section>
            )}

            {recipe.method.length > 0 && (
                <section>
                    <h2>Method</h2>
                    <ol className="steps">
                        {recipe.method.map((step, index) => (
                            <li key={index}>{step}</li>
                        ))}
                    </ol>
                </section>
            )}

            {recipe.notes && (
                <section>
                    <h2>Notes</h2>
                    <p className="body">{recipe.notes}</p>
                </section>
            )}

            {recipe.source && (
                <p className="hint">
                    Source: <span className="val">{recipe.source}</span>
                </p>
            )}

            <div className="actions">
                <button onClick={onEdit}>Edit</button>
                <button className="danger" onClick={onDelete}>
                    Delete
                </button>
            </div>
        </>
    );
}

// ── Editor ───────────────────────────────────────────────────────────────────

function Editor({
    initial,
    onCancel,
    onSave,
}: {
    initial: RecipeDraft;
    onCancel: () => void;
    onSave: (draft: RecipeDraft) => Promise<void>;
}) {
    const [draft, setDraft] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const set = <K extends keyof RecipeDraft>(field: K) => (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => setDraft((previous) => ({ ...previous, [field]: event.target.value }));

    const submit = async () => {
        setSaving(true);
        setProblem(null);
        try {
            await onSave(draft);
        } catch (caught) {
            setProblem(caught instanceof Error ? caught.message : 'Save failed.');
            setSaving(false);
        }
    };

    return (
        <>
            <button className="back" onClick={onCancel}>
                ← Cancel
            </button>

            <h1>{initial.id ? 'Edit recipe' : 'New recipe'}</h1>
            {problem && <p className="alert">{problem}</p>}

            <label className="field">
                <span>Title</span>
                <input value={draft.title} onChange={set('title')} autoFocus />
            </label>

            <div className="pair">
                <label className="field">
                    <span>Serves</span>
                    <input value={draft.servings} onChange={set('servings')} placeholder="2" />
                </label>
                <label className="field">
                    <span>Time</span>
                    <input value={draft.totalTime} onChange={set('totalTime')} placeholder="40 min" />
                </label>
            </div>

            <label className="field">
                <span>Ingredients — one per line</span>
                <textarea rows={8} value={draft.ingredients} onChange={set('ingredients')} />
            </label>

            <label className="field">
                <span>Method — one step per line</span>
                <textarea rows={8} value={draft.method} onChange={set('method')} />
            </label>

            <label className="field">
                <span>Tags — one per line</span>
                <textarea rows={3} value={draft.tags} onChange={set('tags')} placeholder={'quick\ndairy-free'} />
            </label>

            <label className="field">
                <span>Notes</span>
                <textarea rows={4} value={draft.notes} onChange={set('notes')} />
            </label>

            <label className="field">
                <span>Source</span>
                <input value={draft.source} onChange={set('source')} placeholder="Mum, or a URL" />
            </label>

            <div className="actions">
                <button
                    className="primary"
                    disabled={saving || !draft.title.trim()}
                    onClick={submit}
                >
                    {saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={onCancel} disabled={saving}>
                    Cancel
                </button>
            </div>
        </>
    );
}
